import React, {useCallback, useEffect, useState} from "react";
import {AnimatePresence, motion} from 'framer-motion';
import ReactGA from "react-ga4";
import styles from "./style.module.css";
import {t} from "../../intl/Intl";
import {PRIVACY_POLICY_LINK} from "../../enviroment";

const COOKIE_CONSENT_KEY = 'cookie_consent_status';
const CONSENT_WAIT_TIME = 500;

const CookieConsent = () => {
    const [show, setShow] = useState(false);

    // Set default consent state
    useEffect(() => {
        ReactGA.gtag("set", "url_passthrough", true);

        ReactGA.gtag("consent", "default", {
            analytics_storage: "denied",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            wait_for_update: CONSENT_WAIT_TIME
        });
    }, []);

    const grantConsent = useCallback(() => {
        // Enable advanced mode settings
        ReactGA.gtag("set", "ads_data_redaction", false);

        // Update consent state
        ReactGA.gtag("consent", "update", {
            analytics_storage: "granted",
            ad_storage: "granted",
            ad_user_data: "granted",
            ad_personalization: "granted"
        });
    }, []);

    const denyConsent = useCallback(() => {
        // Enable data redaction in restricted mode
        ReactGA.gtag("set", "ads_data_redaction", true);

        // Update consent state
        ReactGA.gtag("consent", "update", {
            analytics_storage: "denied",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied"
        });
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const cookieConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
            if (cookieConsent == null) {
                setShow(true);
            } else if (cookieConsent === 'accepted') {
                grantConsent();
            } else if (cookieConsent === 'rejected') {
                denyConsent();
            }
        }, CONSENT_WAIT_TIME / 3);
        return () => clearTimeout(timeout);
    }, [grantConsent, denyConsent]);

    const onAccept = useCallback(() => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
        setShow(false);
        grantConsent();
    }, [grantConsent]);

    const onReject = useCallback(() => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
        setShow(false);
        denyConsent();
    }, [denyConsent]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className={styles.cookieOverlay}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                >
                    <motion.div
                        className={styles.cookieContainer}
                        initial={{y: 100, opacity: 0}}
                        animate={{y: 0, opacity: 1}}
                        exit={{y: 100, opacity: 0}}
                        role="dialog"
                        aria-labelledby="cookie-title"
                        aria-describedby="cookie-description"
                    >
                        <div className={styles.cookieContent}>
                            <h3 id="cookie-title" className={styles.cookieTitle}>
                                {t('cookieConsentTitle')}
                            </h3>
                            <p id="cookie-description" className={styles.cookieText}>
                                {t('cookieConsentText')}{' '}
                                <a 
                                    href="https://business.safety.google/privacy/" 
                                    target="_blank" 
                                    rel="noreferrer noopener"
                                    className={styles.cookieLink}
                                >
                                    Google Privacy
                                </a>
                                <span className={styles.separator}>•</span>
                                <a 
                                    href={PRIVACY_POLICY_LINK} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className={styles.cookieLink}
                                >
                                    {t('cookiePolicy')}
                                </a>
                            </p>

                            <div className={styles.cookieButtons}>
                                <motion.button
                                    className={`${styles.button} ${styles.rejectButton}`}
                                    whileHover={{scale: 1.02}}
                                    whileTap={{scale: 0.98}}
                                    onClick={onReject}
                                >
                                    {t('cookieConsentReject')}
                                </motion.button>
                                <motion.button
                                    className={`${styles.button} ${styles.acceptButton}`}
                                    whileHover={{scale: 1.02}}
                                    whileTap={{scale: 0.98}}
                                    onClick={onAccept}
                                >
                                    {t('cookieConsentAccept')}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieConsent; 